/**
 * kevia-relationships/index.js
 * Unified entry point for the Relational Pastoral Memory layer.
 *
 * Called from runtimeCognitiveLayer when an entity context is active.
 * Computes [relationship: ...] and [trajectory: ...] context strings
 * for LLM injection.
 */

import { recordEntitySignal, getEntityHistory } from "./relationalMemoryEngine";
import { resolveRelationshipState }             from "./relationshipStateResolver";
import { analyzeTrajectory }                    from "./pastoralTrajectoryAnalyzer";
import { detectPastoralSignals }               from "./predictivePastoralSignals";
import { buildJourneyNarrative }               from "./spiritualJourneyTimeline";

const MAX_RELATIONSHIP_CHARS = 220;
const MAX_TRAJECTORY_CHARS   = 180;

/**
 * Build the full relational context for a given entity.
 *
 * Designed to be fast (< 5ms): reads from localStorage, all pure functions.
 * Should be called AFTER tool execution so visitRows may be available.
 *
 * @param {{
 *   entityContext: { id: string, type: string, name: string, tone: string } | null,
 *   visitRows:     Object[] | null,
 * }} params
 * @returns {{
 *   relationshipContext: string | null,   // "[relationship: ...]"
 *   trajectoryContext:   string | null,   // "[trajectory: ...]"
 *   riskSignals:         number,          // count for debug bar
 * }}
 */
export function buildEntityRelationshipContext({ entityContext, visitRows }) {
  if (!entityContext?.id) {
    return { relationshipContext: null, trajectoryContext: null, riskSignals: 0 };
  }

  const { id, type, name, tone = "neutral" } = entityContext;

  // ── Load historical signals ───────────────────────────────────────────────
  const history    = getEntityHistory(id);
  const prevTone   = history?.signals?.[0]?.tone ?? null;

  // ── Compute relationship state ────────────────────────────────────────────
  const state = resolveRelationshipState({ entityContext, visitRows, prevSignalTone: prevTone });

  // ── Save current signal ───────────────────────────────────────────────────
  recordEntitySignal(id, type, name, {
    ts:          Date.now(),
    tone,
    visitDays:   state.last_contact_days,
    prayerCount: 0,  // prayer count not reliably available at this point
    source:      state.source,
  });

  // ── Reload history after recording (includes current signal) ─────────────
  const updatedHistory = getEntityHistory(id);

  // ── Analyze trajectory ────────────────────────────────────────────────────
  const trajectory = analyzeTrajectory({ state, history: updatedHistory, tone });

  // ── Detect predictive signals ─────────────────────────────────────────────
  const prediction = detectPastoralSignals({
    state,
    trajectory,
    history:    updatedHistory,
    tone,
    entityType: type,
  });

  // ── Build journey narrative ───────────────────────────────────────────────
  const journeyNarrative = buildJourneyNarrative({
    history:     updatedHistory,
    visitRows,
    entityType:  type,
    currentTone: tone,
  });

  // ── Format context strings ────────────────────────────────────────────────
  const relationshipContext = _buildRelationshipLine(name, state, prediction);
  const trajectoryContext   = _buildTrajectoryLine(trajectory, journeyNarrative);

  return {
    relationshipContext: relationshipContext
      ? `[relationship: ${relationshipContext.slice(0, MAX_RELATIONSHIP_CHARS)}]`
      : null,
    trajectoryContext: trajectoryContext
      ? `[trajectory: ${trajectoryContext.slice(0, MAX_TRAJECTORY_CHARS)}]`
      : null,
    riskSignals: prediction.count,
  };
}

// ── Formatting ────────────────────────────────────────────────────────────────

function _buildRelationshipLine(name, state, prediction) {
  const parts = [];

  if (state.last_contact_days !== null && state.last_contact_days > 14) {
    parts.push(`sin visita hace ${state.last_contact_days} días`);
  }

  // Top pastoral signal (most important one)
  if (prediction.signals.length > 0) {
    parts.push(prediction.signals[0].replace(/\.$/, "").toLowerCase());
  }

  if (parts.length === 0) return null;
  return `${name} — ${parts.join("; ")}`;
}

function _buildTrajectoryLine(trajectory, journeyNarrative) {
  if (trajectory.stage === "estable" || trajectory.stage === "desconocido") {
    // Don't waste tokens on unremarkable states
    return journeyNarrative ?? null;
  }

  const parts = [];
  if (trajectory.narrative) parts.push(trajectory.narrative);
  if (journeyNarrative)     parts.push(journeyNarrative);

  return parts.filter(Boolean).join(" ") || null;
}
